import { ItemInstance, TreeInstance } from '@headless-tree/core';
import React, { forwardRef, useEffect, useRef } from 'react';
import { TaskCard } from 'shared/components/connected/task-card';
import {
  InternalTreeItem,
  useDragPreview,
} from 'shared/components/ui/virtualized-tree-list';
import { useDisableTaskOutlineQuery } from 'shared/hooks/queries/use-disable-task-outline-query';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { Task } from 'shared/types/task';
import { Environments, isEnvironment } from 'shared/utils/is-environment';

import { useContextMenu } from '../../context-menu-context';
import * as Styled from './tree-task-card.style';

// The TreeCardContainer shows a border around the card when rerendered on Development and Local.
// On Beta and Production, this code is excluded
const TreeCardContainer =
  !isEnvironment(Environments.Development) && !isEnvironment(Environments.Local)
    ? Styled.TreeItemContainer
    : forwardRef<
        HTMLDivElement,
        React.ComponentProps<typeof Styled.TreeItemContainer>
      >((props, ref) => {
        const containerRef = useForwardedRef(ref);
        const isPreview = useDragPreview();
        const { data: isOutlineDisabled } = useDisableTaskOutlineQuery();

        useEffect(() => {
          if (
            !isEnvironment(Environments.Development) &&
            !isEnvironment(Environments.Local)
          ) {
            return;
          }

          if (isOutlineDisabled) {
            return;
          }

          if (containerRef.current && !isPreview) {
            const randomColor =
              '#' + Math.floor(Math.random() * 16777215).toString(16);
            containerRef.current.style.outline = `2px solid ${randomColor}`;
            containerRef.current.style.transition = 'none';

            setTimeout(() => {
              if (containerRef.current) {
                containerRef.current.style.outline = 'none';
                containerRef.current.style.transition = 'outline 0.5s ease-out';
              }
            }, 200);
          }
        });

        return <Styled.TreeItemContainer ref={containerRef} {...props} />;
      });

export type TreeTaskCardProps = React.ComponentProps<typeof TaskCard> & {
  tree: TreeInstance<InternalTreeItem>;
  item: ItemInstance<InternalTreeItem>;
  tasks: Task[];
  isMultiSelectModifierPressed?: boolean;
};

export const TreeTaskCard: React.FC<TreeTaskCardProps> = ({
  tree,
  item,
  tasks,
  hideContextMenu,
  isMultiSelectModifierPressed,
  ...taskCardProps
}) => {
  const _isMultiSelected =
    hideContextMenu || tree.getSelectedItems().length > 1;
  const selectedTaskItems = useRef<ItemInstance<InternalTreeItem>[]>([]);
  const selectedTasks = useRef<Task[]>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const isPreview = useDragPreview();

  // connection with virtualized-tree-list
  const { open } = useContextMenu();

  const handleContextMenu = (e: React.MouseEvent) => {
    // stop the browser's default context menu
    e.preventDefault();
    e.stopPropagation();

    // create a task-map for easy access and prevent loops in loops
    const tasksMap = new Map(tasks.map((task) => [task.id, task]));

    // get all selected tasks
    selectedTaskItems.current = [...tree.getSelectedItems()];
    selectedTasks.current = selectedTaskItems.current
      .map((item) => tasksMap.get(item.getItemData().sourceId))
      .filter(Boolean) as Task[];

    // select item if not selected
    if (!item.isSelected()) {
      tree.setSelectedItems([item.getId()]);
      selectedTaskItems.current.push(item);
      const task = tasksMap.get(item.getItemData().sourceId);
      task && selectedTasks.current.push(task);
    }

    // get the selected items combined
    open(
      { left: e.clientX, top: e.clientY },
      {
        tasks: selectedTasks.current,
      },
    );
  };

  return (
    <TreeCardContainer ref={containerRef} onContextMenu={handleContextMenu}>
      <TaskCard
        {...taskCardProps}
        isMultiSelectable
        hideContextMenu={_isMultiSelected || isPreview}
        isSelected={isPreview ? false : item.isSelected()}
        preventInteraction={isMultiSelectModifierPressed}
      />
    </TreeCardContainer>
  );
};
